import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Zap, Scale, Users, Target, Clock, CheckCircle, Lock } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import usePageSEO from '@/hooks/usePageSEO';

const AboutUs = () => {
  const navigate = useNavigate();

  usePageSEO({
    title: 'About Us - AI Legal Tools Built Exclusively for Lawyers | Lawra AI',
    description: 'Lawra AI is a legal technology platform built exclusively for solicitors and lawyers. Learn about our mission to transform legal practice with purpose-built AI tools.',
    path: '/about',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Lawra AI",
      "url": "https://lawra-ai.com",
      "description": "AI-powered legal platform built exclusively for solicitors and lawyers.",
      "foundingDate": "2024",
      "sameAs": [],
    },
  });

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      {/* Hero */}
      <section className="pricing-hero" data-testid="about-hero">
        <div className="pricing-hero-inner">
          <p className="uc-hero-label">About Us</p>
          <h1 className="uc-hero-title">AI Built Exclusively for Legal Professionals</h1>
          <p className="uc-hero-subtitle">
            Lawra AI exists for one reason: to give solicitors and lawyers the tools they need to work faster, smarter, and with greater confidence.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="about-story" data-testid="about-story">
        <div className="about-story-inner">
          <div className="about-story-content">
            <h2 className="about-section-title">Our Mission</h2>
            <p className="about-body">
              Legal work demands precision, deep expertise, and relentless attention to detail. But too much of a lawyer's day is consumed by tasks that are repetitive, time-intensive, and low-value — reviewing boilerplate clauses, summarising lengthy documents, drafting standard correspondence.
            </p>
            <p className="about-body">
              We built Lawra AI to change that. Our platform is purpose-built for the legal profession — not adapted from generic AI, not a chatbot with a legal skin. Every tool, every workflow, and every output is designed around how solicitors and lawyers actually work.
            </p>
            <p className="about-body">
              The result: legal professionals save 70-80% of their time on routine tasks, catch risks that manual review misses under pressure, and focus their expertise where it matters most — advising clients, building strategy, and winning outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="about-values" data-testid="about-values">
        <div className="about-values-inner">
          <h2 className="about-section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>What Sets Lawra AI Apart</h2>
          <p className="about-values-subtitle">We don't build AI for everyone. We build it for lawyers.</p>
          <div className="about-values-grid">
            <div className="about-value-card">
              <Target size={28} className="about-value-icon" />
              <h3>Purpose-Built for Law</h3>
              <p>Every tool is designed around legal workflows — contract structures, case law patterns, compliance frameworks, and jurisdictional nuances. This isn't generic AI repurposed for law. It's legal AI from the ground up.</p>
            </div>
            <div className="about-value-card">
              <Zap size={28} className="about-value-icon" />
              <h3>Instant, Actionable Results</h3>
              <p>Upload a document or type your instructions and receive structured, professional-grade output in seconds. No waiting, no learning curve. Results you can act on immediately.</p>
            </div>
            <div className="about-value-card">
              <Shield size={28} className="about-value-icon" />
              <h3>Accuracy You Can Trust</h3>
              <p>Our AI is trained to understand legal language with the precision lawyers demand. Risk clauses, compliance gaps, and material issues are flagged consistently — not buried in generic summaries.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Lawra AI */}
      <section className="about-why" data-testid="about-why-choose">
        <div className="about-why-inner">
          <h2 className="about-section-title" style={{ textAlign: 'center', marginBottom: '2.5rem' }}>Why Choose Lawra AI</h2>
          <div className="about-why-grid">
            <div className="about-why-item">
              <div className="about-why-icon-wrap">
                <Clock size={22} />
              </div>
              <div>
                <h4>Save 70-80% of Your Time</h4>
                <p>Tasks that take hours manually — contract review, document summarisation, legal research — are completed in under a minute. Reclaim your day for high-value work.</p>
              </div>
            </div>
            <div className="about-why-item">
              <div className="about-why-icon-wrap">
                <Scale size={22} />
              </div>
              <div>
                <h4>Jurisdiction-Aware Intelligence</h4>
                <p>Lawra AI understands UK legal standards and supports multiple jurisdictions. Your outputs are contextually appropriate, not generic boilerplate.</p>
              </div>
            </div>
            <div className="about-why-item">
              <div className="about-why-icon-wrap">
                <Users size={22} />
              </div>
              <div>
                <h4>Built for Firms of All Sizes</h4>
                <p>Whether you're a sole practitioner or a 200-person firm, Lawra AI scales with your practice. Flexible plans from free trials to enterprise solutions.</p>
              </div>
            </div>
            <div className="about-why-item">
              <div className="about-why-icon-wrap">
                <CheckCircle size={22} />
              </div>
              <div>
                <h4>10+ Specialised Tools</h4>
                <p>Contract review, due diligence, legal research, document generation, deposition prep, email drafting, and more — a comprehensive suite covering every aspect of practice.</p>
              </div>
            </div>
            <div className="about-why-item">
              <div className="about-why-icon-wrap">
                <Lock size={22} />
              </div>
              <div>
                <h4>Secure and Confidential</h4>
                <p>Your documents and data are protected with enterprise-grade security. Client confidentiality is not a feature — it's a foundation.</p>
              </div>
            </div>
            <div className="about-why-item">
              <div className="about-why-icon-wrap">
                <Zap size={22} />
              </div>
              <div>
                <h4>No Learning Curve</h4>
                <p>If you can use email, you can use Lawra AI. Upload, click, and receive results. No training, no onboarding sessions, no configuration.</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2.5rem' }}>
            <Button className="try-now-btn" size="lg" data-testid="about-why-cta" onClick={() => navigate('/use-cases')}>
              See Real Results from Law Firms <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="about-cta">
        <div className="cta-content">
          <h2>Ready to Work Smarter?</h2>
          <p>Start with 20 free credits. No credit card required.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              className="try-now-btn"
              size="lg"
              data-testid="about-cta-trial"
              onClick={async () => {
                try {
                  const { auth } = await import('../firebase');
                  if (auth.currentUser) navigate('/dashboard');
                  else navigate('/onboarding');
                } catch { navigate('/onboarding'); }
              }}
            >
              Start Free Trial
            </Button>
            <Button variant="ghost" className="use-cases-nav-link" size="lg" onClick={() => navigate('/use-cases')} data-testid="about-cta-cases">
              See Use Cases <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
